import { autoUpdate, offset, shift, useFloating } from "@floating-ui/react";
import { FocusTrap } from "focus-trap-react";
import { FunctionComponent, useCallback, useState } from "react";
import { CSSTransition } from "react-transition-group";

import Button from "@ui/components/cta/Button";
import CheckmarkIcon from "@ui/components/icon/CheckmarkIcon";
import CogIcon from "@ui/components/icon/CogIcon";
import DotsHorizIcon from "@ui/components/icon/DotsHorizIcon";
import NotificationsPageIcon from "@ui/components/icon/NotificationsPageIcon";
import theme from "@ui/themes/theme-contract.css";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useNotificationsStore } from "@voxmedia/duet-context/context/notifications-context";

import * as styles from "./ThreeDotsMenu.css";

interface ThreeDotsMenuProps {
  showGoToNotificationsPageLink?: boolean;
  tabIndex: number;
}

const ACTIONS_TRIGGER_SELECTOR = "[data-notifications-actions-trigger='true']";

const ThreeDotsMenu: FunctionComponent<ThreeDotsMenuProps> = ({
  showGoToNotificationsPageLink = true,
  tabIndex,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { RootStore } = useNotificationsStore();
  const analytics = useAnalytics();

  const { refs, floatingStyles } = useFloating({
    middleware: [offset(10), shift()],
    whileElementsMounted: autoUpdate,
  });
  const onClickThreeDots = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);
  const onClickNotificationSettings = useCallback(() => {
    window.location.href = "/account/notification-settings";
  }, []);
  const onClickMarkAllAsRead = useCallback(async () => {
    if (!RootStore) {
      return;
    }
    await RootStore.notificationsStore.markRead({
      all: true,
    });
    RootStore.unreadNotificationsStore.setHasNext(false);
    RootStore.unreadNotificationsStore.setCursor(null);
  }, [RootStore]);
  const onClickGoToNotificationsPage = useCallback(() => {
    analytics?.emit({
      event: "notification_page_click",
      notification_page_click: {
        click_text: "Go to Notifications Page",
      },
    });
    window.location.href = "/notifications";
  }, [analytics]);
  return (
    <>
      <button
        type="button"
        aria-label="Notifications actions"
        aria-expanded={isMenuOpen}
        aria-haspopup="dialog"
        onClick={onClickThreeDots}
        ref={refs.setReference}
        tabIndex={tabIndex}
        data-notifications-actions-trigger="true"
        className={styles.triggerButton}
      >
        <DotsHorizIcon
          className="three-dots-icon"
          fill={theme.sys.color.subtle}
        />
      </button>
      <CSSTransition
        in={isMenuOpen}
        timeout={200}
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActive,
          exit: styles.exit,
          exitActive: styles.exitActive,
        }}
        nodeRef={refs.floating}
        unmountOnExit
      >
        <FocusTrap
          active={isMenuOpen}
          focusTrapOptions={{
            fallbackFocus: `#notifications-actions`,
            allowOutsideClick: true,
            clickOutsideDeactivates: (e: MouseEvent | TouchEvent) => {
              if (e.target instanceof HTMLElement) {
                if (!e.target.closest(ACTIONS_TRIGGER_SELECTOR)) {
                  setIsMenuOpen(false);
                }
                return false;
              }
              return false;
            },
          }}
        >
          <div
            id="notifications-actions"
            style={floatingStyles}
            ref={refs.setFloating}
            role="dialog"
            aria-modal="true"
            aria-label="Notifications"
            className={styles.popoverContainer}
          >
            <div className={styles.popoverContent}>
              <Button
                text="Mark all as read"
                onClick={() => {
                  void onClickMarkAllAsRead();
                }}
                iconLeft={<CheckmarkIcon width={14} height={14} />}
                displayType="cycloneSecondary"
                className={styles.button}
              />
              <Button
                text="Notification settings"
                onClick={onClickNotificationSettings}
                iconLeft={<CogIcon width={14} height={14} />}
                displayType="cycloneSecondary"
                className={styles.button}
              />
              {showGoToNotificationsPageLink && (
                <Button
                  text="Go to Notifications Page"
                  onClick={onClickGoToNotificationsPage}
                  iconLeft={<NotificationsPageIcon width={14} height={14} />}
                  displayType="cycloneSecondary"
                  className={styles.button}
                />
              )}
            </div>
          </div>
        </FocusTrap>
      </CSSTransition>
    </>
  );
};

export default ThreeDotsMenu;
